import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, FileText, Edit3, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { IconContainer } from '../../../components/brand/BrandIcon';
import { SmallText } from '../../../components/brand/BrandTypography';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../../../components/brand/BrandButton';
import { PAGE_ICON_MAP, API_URL } from '../constants';
import SectionEditorModal from '../modals/SectionEditorModal';

const PageContentEditor = ({ pageContent, onUpdate }) => {
    const [expanded, setExpanded] = useState(true);
    const [editingPage, setEditingPage] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [isNewSection, setIsNewSection] = useState(false);
    const [pageData, setPageData] = useState(pageContent);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setPageData(pageContent);
    }, [pageContent]);

    const handleSavePage = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/api/pages/${pageData.page_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: pageData.title,
                    subtitle: pageData.subtitle,
                    hero_text: pageData.hero_text,
                    contact_email: pageData.contact_email,
                    cta_primary_text: pageData.cta_primary_text,
                    cta_secondary_text: pageData.cta_secondary_text,
                })
            });
            if (response.ok) {
                onUpdate();
                setEditingPage(false);
                toast.success('Page content updated');
            } else {
                toast.error('Failed to update page');
            }
        } catch (error) {
            toast.error('Failed to update page');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSection = async (sectionData, isNew) => {
        try {
            const response = await fetch(`${API_URL}/api/pages/${pageData.page_id}/sections/${sectionData.section_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sectionData)
            });
            if (response.ok) {
                onUpdate();
                toast.success(isNew ? 'Section added' : 'Section updated');
            } else {
                toast.error('Failed to save section');
            }
        } catch (error) {
            toast.error('Failed to save section');
            throw error;
        }
    };

    const handleDeleteSection = async (sectionId) => {
        if (!window.confirm('Are you sure you want to delete this section?')) return;
        
        try {
            const response = await fetch(`${API_URL}/api/pages/${pageData.page_id}/sections/${sectionId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                onUpdate();
                toast.success('Section deleted');
            } else {
                toast.error('Failed to delete section');
            }
        } catch (error) {
            toast.error('Failed to delete section');
        }
    };

    const sortedSections = [...(pageData?.sections || [])].sort((a, b) => a.order - b.order);

    if (!pageContent) return null;

    return (
        <div className="bg-white rounded-xl border border-[#FFBB98] overflow-hidden">
            <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#FEF6EF]"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-3">
                    <IconContainer variant="sage" size="md">
                        <FileText className="w-5 h-5" />
                    </IconContainer>
                    <div>
                        <h3 className="font-medium text-[#1D6B6B]">{pageData.title}</h3>
                        <p className="text-sm text-[#7D8E95]">Page ID: {pageData.page_id}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-[#7D8E95]">{sortedSections.length} sections</span>
                    {expanded ? <ChevronDown className="w-5 h-5 text-[#7D8E95]" /> : <ChevronRight className="w-5 h-5 text-[#7D8E95]" />}
                </div>
            </div>

            {expanded && (
                <div className="border-t border-[#FFBB98] p-4">
                    {editingPage ? (
                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Page Title</label>
                                <input
                                    type="text"
                                    value={pageData.title}
                                    onChange={(e) => setPageData({...pageData, title: e.target.value})}
                                    className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Subtitle</label>
                                <input
                                    type="text"
                                    value={pageData.subtitle || ''}
                                    onChange={(e) => setPageData({...pageData, subtitle: e.target.value})}
                                    className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Hero Text</label>
                                <textarea
                                    value={pageData.hero_text || ''}
                                    onChange={(e) => setPageData({...pageData, hero_text: e.target.value})}
                                    className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] text-sm min-h-[80px]"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Contact Email</label>
                                    <input
                                        type="email"
                                        value={pageData.contact_email || ''}
                                        onChange={(e) => setPageData({...pageData, contact_email: e.target.value})}
                                        className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[#1D6B6B] mb-1">Primary CTA</label>
                                    <input
                                        type="text"
                                        value={pageData.cta_primary_text || ''}
                                        onChange={(e) => setPageData({...pageData, cta_primary_text: e.target.value})}
                                        className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] text-sm"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <TertiaryButton onClick={() => { setEditingPage(false); setPageData(pageContent); }} className="flex-1">
                                    Cancel
                                </TertiaryButton>
                                <PrimaryButton onClick={handleSavePage} disabled={saving} className="flex-1">
                                    {saving ? 'Saving...' : 'Save Page Details'}
                                </PrimaryButton>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-6">
                            <div className="space-y-2 mb-4">
                                <div className="text-sm">
                                    <span className="text-[#7D8E95]">Title:</span>{' '}
                                    <span className="text-[#1D6B6B]">{pageData.title}</span>
                                </div>
                                <div className="text-sm">
                                    <span className="text-[#7D8E95]">Subtitle:</span>{' '}
                                    <span className="text-[#1D6B6B]">{pageData.subtitle || '—'}</span>
                                </div>
                                <div className="text-sm">
                                    <span className="text-[#7D8E95]">Hero:</span>{' '}
                                    <span className="text-[#1D6B6B] line-clamp-2">{pageData.hero_text || '—'}</span>
                                </div>
                            </div>
                            <SecondaryButton onClick={() => setEditingPage(true)} className="w-full">
                                <Edit3 className="w-4 h-4 mr-2" />
                                Edit Page Details
                            </SecondaryButton>
                        </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <SmallText className="font-medium text-[#1D6B6B]">Sections</SmallText>
                            <button
                                onClick={() => { setEditingSection({ order: sortedSections.length }); setIsNewSection(true); }}
                                className="flex items-center gap-1 text-sm text-[#1D6B6B] hover:text-[#145555]"
                            >
                                <Plus className="w-4 h-4" />
                                Add Section
                            </button>
                        </div>
                        <div className="space-y-3">
                            {sortedSections.map((section) => {
                                const IconComponent = section.icon ? PAGE_ICON_MAP[section.icon] : null;
                                return (
                                    <div 
                                        key={section.section_id} 
                                        className={`bg-[#FEF6EF] rounded-lg p-3 ${!section.is_visible ? 'opacity-50' : ''}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <span className="w-6 h-6 rounded-full bg-[#7D8E95]/30 flex items-center justify-center text-[#1D6B6B] text-xs">
                                                    {section.order + 1}
                                                </span>
                                                {IconComponent && (
                                                    <IconComponent className="w-4 h-4 text-[#1D6B6B] flex-shrink-0" />
                                                )}
                                                <div className="min-w-0">
                                                    <h4 className="font-medium text-sm text-[#1D6B6B] truncate">{section.title}</h4>
                                                    <p className="text-xs text-[#7D8E95] truncate">{section.content.substring(0, 60)}...</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                {!section.is_visible && (
                                                    <span className="text-xs text-[#7D8E95] mr-1">Hidden</span>
                                                )}
                                                <button
                                                    onClick={() => { setEditingSection(section); setIsNewSection(false); }}
                                                    className="p-1.5 hover:bg-white rounded text-[#7D8E95]"
                                                >
                                                    <Edit3 className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteSection(section.section_id)}
                                                    className="p-1.5 hover:bg-red-50 rounded text-red-500"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                        {section.quote && (
                                            <p className="mt-2 text-xs text-[#7D8E95] italic pl-8">"{section.quote.substring(0, 80)}..."</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {editingSection && (
                <SectionEditorModal
                    section={editingSection}
                    isNew={isNewSection}
                    onSave={handleSaveSection}
                    onClose={() => { setEditingSection(null); setIsNewSection(false); }}
                />
            )}
        </div>
    );
};

export default PageContentEditor;
